precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 180;

// Fire/passion colors
vec3 fireColor(float t) {
  vec3 a = vec3(0.5, 0.2, 0.0);
  vec3 b = vec3(0.5, 0.3, 0.0);
  vec3 c = vec3(1.0, 0.5, 0.2);
  vec3 d = vec3(0.0, 0.1, 0.2);
  return a + b * cos(6.28318 * (c * t + d));
}

// Water/calm colors
vec3 waterColor(float t) {
  vec3 a = vec3(0.1, 0.2, 0.3);
  vec3 b = vec3(0.2, 0.3, 0.4);
  vec3 c = vec3(0.5, 0.6, 0.8);
  vec3 d = vec3(0.2, 0.3, 0.4);
  return a + b * cos(6.28318 * (c * t + d));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // Two colliding Julia sets representing fire and water
  // Device tilt shifts the collision point
  vec2 tiltOffset = uDeviceTilt * 0.5;
  vec2 z1 = uv * 2.5 / uZoom + tiltOffset;
  vec2 z2 = uv * 2.5 / uZoom - tiltOffset; // Opposite direction for tension
  
  // Fire Julia constant - more aggressive
  vec2 c1 = vec2(
    -0.8 + sin(uTime * 0.2) * 0.1,
    0.156 + cos(uTime * 0.15) * 0.1
  );
  
  // Water Julia constant - more fluid
  vec2 c2 = vec2(
    0.285 + sin(uTime * 0.12) * 0.05,
    0.01 + cos(uTime * 0.18) * 0.05
  );
  
  float iter1 = 0.0;
  float iter2 = 0.0;
  
  // Fire fractal
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z1, z1) > 4.0) break;
    float xTemp = z1.x * z1.x - z1.y * z1.y + c1.x;
    z1.y = abs(2.0 * z1.x * z1.y) + c1.y; // Burning ship variant
    z1.x = xTemp;
    iter1 += 1.0;
  }
  
  // Water fractal  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z2, z2) > 4.0) break;
    float xTemp = z2.x * z2.x - z2.y * z2.y + c2.x;
    z2.y = 2.0 * z2.x * z2.y + c2.y;
    z2.x = xTemp;
    iter2 += 1.0;
  }
  
  float t1 = iter1 / float(MAX_ITERATIONS);
  float t2 = iter2 / float(MAX_ITERATIONS);
  
  // Get colors for each element
  vec3 fire = fireColor(t1 * 2.0 + uTime * 0.1);
  vec3 water = waterColor(t2 * 2.0 - uTime * 0.08);
  
  // The "edge" where they meet - zone of tension
  float edge = abs(t1 - t2);
  float edgeIntensity = 1.0 - smoothstep(0.0, 0.3, edge);
  
  // Mix based on which fractal "dominates" at this point
  float dominance = smoothstep(-0.2, 0.2, t1 - t2);
  vec3 col = mix(water, fire, dominance);
  
  // At the edge, create tension zone with accent color
  vec3 tensionColor = uColorAccent * (1.0 + uBass * 0.5);
  col = mix(col, tensionColor, edgeIntensity * 0.7);
  
  // Add energy at collision points
  if (edge < 0.1) {
    col += vec3(1.0, 0.9, 0.7) * (0.1 - edge) * 3.0 * (1.0 + uMid);
  }
  
  // Audio makes the "battle" more intense (stronger)
  col *= 1.0 + uAudioAverage * 0.8;
  col += uColorAccent * uBass * 0.3;
  
  // Pulsing glow at edges
  float pulse = 0.5 + 0.5 * sin(uTime * 3.0 + edge * 10.0);
  col += edgeIntensity * pulse * uColorAccent * 0.3;
  
  // Deep shadows for drama
  float shadow = smoothstep(1.0, 0.0, max(t1, t2));
  col = mix(col, uColorPrimary * 0.1, shadow * 0.5);
  
  // Vignette
  vec2 vigUv = vUv * 2.0 - 1.0;
  float vignette = 1.0 - dot(vigUv, vigUv) * 0.35;
  col *= vignette;
  
  gl_FragColor = vec4(col, 1.0);
}

